/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
var __decorate = this && this.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
    r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
    d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { $, append, createStyleSheet, EventHelper, getElementsByTagName } from '../../dom.js';
import { DomEmitter } from '../../event.js';
import { EventType, Gesture } from '../../touch.js';
import { Delayer } from '../../../common/async.js';
import { memoize } from '../../../common/decorators.js';
import { Emitter, Event } from '../../../common/event.js';
import { Disposable, DisposableStore, toDisposable } from '../../../common/lifecycle.js';
import { isMacintosh } from '../../../common/platform.js';
import './sash.css';
let DEBUG = false;
export var OrthogonalEdge;
(function (OrthogonalEdge) {
  OrthogonalEdge["North"] = "north";
  OrthogonalEdge["South"] = "south";
  OrthogonalEdge["East"] = "east";
  OrthogonalEdge["West"] = "west";
})(OrthogonalEdge || (OrthogonalEdge = {}));
let globalSize = 4;
const onDidChangeGlobalSize = new Emitter();
let globalHoverDelay = 300;
const onDidChangeHoverDelay = new Emitter();
class MouseEventFactory {
  constructor() {
    this.disposables = new DisposableStore();
  }
  get onPointerMove() {
    return this.disposables.add(new DomEmitter(window, 'mousemove')).event;
  }
  get onPointerUp() {
    return this.disposables.add(new DomEmitter(window, 'mouseup')).event;
  }
  dispose() {
    this.disposables.dispose();
  }
}
__decorate([memoize], MouseEventFactory.prototype, "onPointerMove", null);
__decorate([memoize], MouseEventFactory.prototype, "onPointerUp", null);
class GestureEventFactory {
  constructor(el) {
    this.el = el;
    this.disposables = new DisposableStore();
  }
  get onPointerMove() {
    return this.disposables.add(new DomEmitter(this.el, EventType.Change)).event;
  }
  get onPointerUp() {
    return this.disposables.add(new DomEmitter(this.el, EventType.End)).event;
  }
  dispose() {
    this.disposables.dispose();
  }
}
__decorate([memoize], GestureEventFactory.prototype, "onPointerMove", null);
__decorate([memoize], GestureEventFactory.prototype, "onPointerUp", null);
class OrthogonalPointerEventFactory {
  constructor(factory) {
    this.factory = factory;
  }
  get onPointerMove() {
    return this.factory.onPointerMove;
  }
  get onPointerUp() {
    return this.factory.onPointerUp;
  }
  dispose() {
    // noop
  }
}
__decorate([memoize], OrthogonalPointerEventFactory.prototype, "onPointerMove", null);
__decorate([memoize], OrthogonalPointerEventFactory.prototype, "onPointerUp", null);
export class Sash extends Disposable {
  constructor(container, layoutProvider, options) {
    super();
    this.hoverDelay = globalHoverDelay;
    this.hoverDelayer = this._register(new Delayer(this.hoverDelay));
    this._state = 3 /* Enabled */;
    this._onDidEnablementChange = this._register(new Emitter());
    this.onDidEnablementChange = this._onDidEnablementChange.event;
    this._onDidStart = this._register(new Emitter());
    this.onDidStart = this._onDidStart.event;
    this._onDidChange = this._register(new Emitter());
    this.onDidChange = this._onDidChange.event;
    this._onDidReset = this._register(new Emitter());
    this.onDidReset = this._onDidReset.event;
    this._onDidEnd = this._register(new Emitter());
    this.onDidEnd = this._onDidEnd.event;
    this.linkedSash = undefined;
    this.orthogonalStartSashDisposables = this._register(new DisposableStore());
    this.orthogonalStartDragHandleDisposables = this._register(new DisposableStore());
    this.orthogonalEndSashDisposables = this._register(new DisposableStore());
    this.orthogonalEndDragHandleDisposables = this._register(new DisposableStore());
    this.el = append(container, $('.monaco-sash'));
    if (options.orthogonalEdge) {
      this.el.classList.add(`orthogonal-edge-${options.orthogonalEdge}`);
    }
    if (isMacintosh) {
      this.el.classList.add('mac');
    }
    const onMouseDown = this._register(new DomEmitter(this.el, 'mousedown')).event;
    this._register(onMouseDown(e => this.onPointerStart(e, new MouseEventFactory()), this));
    const onMouseDoubleClick = this._register(new DomEmitter(this.el, 'dblclick')).event;
    this._register(onMouseDoubleClick(this.onPointerDoublePress, this));
    const onMouseEnter = this._register(new DomEmitter(this.el, 'mouseenter')).event;
    this._register(onMouseEnter(() => Sash.onMouseEnter(this)));
    const onMouseLeave = this._register(new DomEmitter(this.el, 'mouseleave')).event;
    this._register(onMouseLeave(() => Sash.onMouseLeave(this)));
    this._register(Gesture.addTarget(this.el));
    const onTouchStart = Event.map(this._register(new DomEmitter(this.el, EventType.Start)).event, e => {
      var _a;
      return Object.assign(Object.assign({}, e), {
        target: (_a = e.initialTarget) !== null && _a !== void 0 ? _a : null
      });
    });
    this._register(onTouchStart(e => this.onPointerStart(e, new GestureEventFactory(this.el)), this));
    const onTap = this._register(new DomEmitter(this.el, EventType.Tap)).event;
    const onDoubleTap = Event.map(Event.filter(Event.debounce(onTap, (res, event) => {
      var _a;
      return {
        event,
        count: ((_a = res === null || res === void 0 ? void 0 : res.count) !== null && _a !== void 0 ? _a : 0) + 1
      };
    }, 250), _ref => {
      let {
        count
      } = _ref;
      return count === 2;
    }), _ref2 => {
      let {
        event
      } = _ref2;
      var _a;
      return Object.assign(Object.assign({}, event), {
        target: (_a = event.initialTarget) !== null && _a !== void 0 ? _a : null
      });
    });
    this._register(onDoubleTap(this.onPointerDoublePress, this));
    if (typeof options.size === 'number') {
      this.size = options.size;
      if (options.orientation === 0 /* VERTICAL */) {
        this.el.style.width = `${this.size}px`;
      } else {
        this.el.style.height = `${this.size}px`;
      }
    } else {
      this.size = globalSize;
      this._register(onDidChangeGlobalSize.event(size => {
        this.size = size;
        this.layout();
      }));
    }
    this._register(onDidChangeHoverDelay.event(delay => this.hoverDelay = delay));
    this.hidden = false;
    this.layoutProvider = layoutProvider;
    this.orthogonalStartSash = options.orthogonalStartSash;
    this.orthogonalEndSash = options.orthogonalEndSash;
    this.orientation = options.orientation || 0 /* VERTICAL */;
    if (this.orientation === 1 /* HORIZONTAL */) {
      this.el.classList.add('horizontal');
      this.el.classList.remove('vertical');
    } else {
      this.el.classList.remove('horizontal');
      this.el.classList.add('vertical');
    }
    this.el.classList.toggle('debug', DEBUG);
    this.layout();
  }
  get state() {
    return this._state;
  }
  set state(state) {
    if (this._state === state) {
      return;
    }
    this.el.classList.toggle('disabled', state === 0 /* Disabled */);
    this.el.classList.toggle('minimum', state === 1 /* Minimum */);
    this.el.classList.toggle('maximum', state === 2 /* Maximum */);
    this._state = state;
    this._onDidEnablementChange.fire(state);
  }
  get orthogonalStartSash() {
    return this._orthogonalStartSash;
  }
  set orthogonalStartSash(sash) {
    this.orthogonalStartDragHandleDisposables.clear();
    this.orthogonalStartSashDisposables.clear();
    if (sash) {
      const onChange = state => {
        this.orthogonalStartDragHandleDisposables.clear();
        if (state !== 0 /* Disabled */) {
          this._orthogonalStartDragHandle = append(this.el, $('.orthogonal-drag-handle.start'));
          this.orthogonalStartDragHandleDisposables.add(toDisposable(() => this._orthogonalStartDragHandle.remove()));
          this.orthogonalStartDragHandleDisposables.add(new DomEmitter(this._orthogonalStartDragHandle, 'mouseenter')).event(() => Sash.onMouseEnter(sash), undefined, this.orthogonalStartDragHandleDisposables);
          this.orthogonalStartDragHandleDisposables.add(new DomEmitter(this._orthogonalStartDragHandle, 'mouseleave')).event(() => Sash.onMouseLeave(sash), undefined, this.orthogonalStartDragHandleDisposables);
        }
      };
      this.orthogonalStartSashDisposables.add(sash.onDidEnablementChange(onChange, this));
      onChange(sash.state);
    }
    this._orthogonalStartSash = sash;
  }
  get orthogonalEndSash() {
    return this._orthogonalEndSash;
  }
  set orthogonalEndSash(sash) {
    this.orthogonalEndDragHandleDisposables.clear();
    this.orthogonalEndSashDisposables.clear();
    if (sash) {
      const onChange = state => {
        this.orthogonalEndDragHandleDisposables.clear();
        if (state !== 0 /* Disabled */) {
          this._orthogonalEndDragHandle = append(this.el, $('.orthogonal-drag-handle.end'));
          this.orthogonalEndDragHandleDisposables.add(toDisposable(() => this._orthogonalEndDragHandle.remove()));
          this.orthogonalEndDragHandleDisposables.add(new DomEmitter(this._orthogonalEndDragHandle, 'mouseenter')).event(() => Sash.onMouseEnter(sash), undefined, this.orthogonalEndDragHandleDisposables);
          this.orthogonalEndDragHandleDisposables.add(new DomEmitter(this._orthogonalEndDragHandle, 'mouseleave')).event(() => Sash.onMouseLeave(sash), undefined, this.orthogonalEndDragHandleDisposables);
        }
      };
      this.orthogonalEndSashDisposables.add(sash.onDidEnablementChange(onChange, this));
      onChange(sash.state);
    }
    this._orthogonalEndSash = sash;
  }
  onPointerStart(event, pointerEventFactory) {
    EventHelper.stop(event);
    let isMultisashResize = false;
    if (!event.__orthogonalSashEvent) {
      const orthogonalSash = this.getOrthogonalSash(event);
      if (orthogonalSash) {
        isMultisashResize = true;
        event.__orthogonalSashEvent = true;
        orthogonalSash.onPointerStart(event, new OrthogonalPointerEventFactory(pointerEventFactory));
      }
    }
    if (this.linkedSash && !event.__linkedSashEvent) {
      event.__linkedSashEvent = true;
      this.linkedSash.onPointerStart(event, new OrthogonalPointerEventFactory(pointerEventFactory));
    }
    if (!this.state) {
      return;
    }
    const iframes = getElementsByTagName('iframe');
    for (const iframe of iframes) {
      iframe.style.pointerEvents = 'none'; // disable mouse events on iframes as long as we drag the sash
    }
    const startX = event.pageX;
    const startY = event.pageY;
    const altKey = event.altKey;
    const startEvent = {
      startX,
      currentX: startX,
      startY,
      currentY: startY,
      altKey
    };
    this.el.classList.add('active');
    this._onDidStart.fire(startEvent);
    // fix https://github.com/microsoft/vscode/issues/21675
    const style = createStyleSheet(this.el);
    const updateStyle = () => {
      let cursor = '';
      if (isMultisashResize) {
        cursor = 'all-scroll';
      } else if (this.orientation === 1 /* HORIZONTAL */) {
        if (this.state === 1 /* Minimum */) {
          cursor = 's-resize';
        } else if (this.state === 2 /* Maximum */) {
          cursor = 'n-resize';
        } else {
          cursor = isMacintosh ? 'row-resize' : 'ns-resize';
        }
      } else {
        if (this.state === 1 /* Minimum */) {
          cursor = 'e-resize';
        } else if (this.state === 2 /* Maximum */) {
          cursor = 'w-resize';
        } else {
          cursor = isMacintosh ? 'col-resize' : 'ew-resize';
        }
      }
      style.textContent = `* { cursor: ${cursor} !important; }`;
    };
    const disposables = new DisposableStore();
    updateStyle();
    if (!isMultisashResize) {
      this.onDidEnablementChange(updateStyle, null, disposables);
    }
    const onPointerMove = e => {
      EventHelper.stop(e, false);
      const event = {
        startX,
        currentX: e.pageX,
        startY,
        currentY: e.pageY,
        altKey
      };
      this._onDidChange.fire(event);
    };
    const onPointerUp = e => {
      EventHelper.stop(e, false);
      this.el.removeChild(style);
      this.el.classList.remove('active');
      this._onDidEnd.fire();
      disposables.dispose();
      for (const iframe of iframes) {
        iframe.style.pointerEvents = 'auto';
      }
    };
    pointerEventFactory.onPointerMove(onPointerMove, null, disposables);
    pointerEventFactory.onPointerUp(onPointerUp, null, disposables);
    disposables.add(pointerEventFactory);
  }
  onPointerDoublePress(e) {
    const orthogonalSash = this.getOrthogonalSash(e);
    if (orthogonalSash) {
      orthogonalSash._onDidReset.fire();
    }
    if (this.linkedSash) {
      this.linkedSash._onDidReset.fire();
    }
    this._onDidReset.fire();
  }
  static onMouseEnter(sash) {
    let fromLinkedSash = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    if (sash.el.classList.contains('active')) {
      sash.hoverDelayer.cancel();
      sash.el.classList.add('hover');
    } else {
      sash.hoverDelayer.trigger(() => sash.el.classList.add('hover'), sash.hoverDelay).then(undefined, () => {});
    }
    if (!fromLinkedSash && sash.linkedSash) {
      Sash.onMouseEnter(sash.linkedSash, true);
    }
  }
  static onMouseLeave(sash) {
    let fromLinkedSash = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    sash.hoverDelayer.cancel();
    sash.el.classList.remove('hover');
    if (!fromLinkedSash && sash.linkedSash) {
      Sash.onMouseLeave(sash.linkedSash, true);
    }
  }
  clearSashHoverState() {
    Sash.onMouseLeave(this);
  }
  layout() {
    if (this.orientation === 0 /* VERTICAL */) {
      const verticalProvider = this.layoutProvider;
      this.el.style.left = verticalProvider.getVerticalSashLeft(this) - this.size / 2 + 'px';
      if (verticalProvider.getVerticalSashTop) {
        this.el.style.top = verticalProvider.getVerticalSashTop(this) + 'px';
      }
      if (verticalProvider.getVerticalSashHeight) {
        this.el.style.height = verticalProvider.getVerticalSashHeight(this) + 'px';
      }
    } else {
      const horizontalProvider = this.layoutProvider;
      this.el.style.top = horizontalProvider.getHorizontalSashTop(this) - this.size / 2 + 'px';
      if (horizontalProvider.getHorizontalSashLeft) {
        this.el.style.left = horizontalProvider.getHorizontalSashLeft(this) + 'px';
      }
      if (horizontalProvider.getHorizontalSashWidth) {
        this.el.style.width = horizontalProvider.getHorizontalSashWidth(this) + 'px';
      }
    }
  }
  hide() {
    this.hidden = true;
    this.el.style.display = 'none';
    this.el.setAttribute('aria-hidden', 'true');
  }
  getOrthogonalSash(e) {
    if (!e.target || !(e.target instanceof HTMLElement)) {
      return undefined;
    }
    if (e.target.classList.contains('orthogonal-drag-handle')) {
      return e.target.classList.contains('start') ? this.orthogonalStartSash : this.orthogonalEndSash;
    }
    return undefined;
  }
  dispose() {
    super.dispose();
    this.el.remove();
  }
}